import { gsap } from 'gsap';
import { getCardVisualSource, captureFrameDataUrl } from './cardVideos.js';
import { getRevealSlowMult, transitionLog } from '../debug/transitionDebug.js';

const MPA_EXPAND_DURATION = 0.85; // chotbat / travel → then navigate
export const INPLACE_EXPAND_DURATION = 0.85;

const REDUCED_MOTION =
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

// ── WebGL wave layer ──────────────────────────────────────────
// The card image is drawn on a subdivided plane that ripples like cloth
// while it grows from the card rect to fullscreen. Displacement peaks
// mid-transition and settles to zero at both ends, so it starts as the
// exact card and lands as a flat fullscreen cover — with a whisper of
// chromatic fringe and scanline while it's in flight.

const VERT_SRC = `
attribute vec2 aPos;            // plane coords in [0,1]
uniform vec4 uRect;             // current rect in px (x, y, w, h)
uniform vec2 uViewport;         // viewport px
uniform float uWave;            // displacement strength (peaks mid-flight)
uniform float uTime;
varying vec2 vUv;
varying float vWave;

void main() {
  vUv = aPos;

  // Cloth ripple — two crossed sine fields, zero at the very edges of the
  // plane so the frame border bends but never tears open.
  float edge = aPos.x * (1.0 - aPos.x) * aPos.y * (1.0 - aPos.y) * 16.0;
  float dx = sin(aPos.y * 9.0 + uTime * 7.0) * uWave * uRect.w * 0.055;
  float dy = sin(aPos.x * 7.0 + uTime * 6.0 + 1.7) * uWave * uRect.z * 0.045;
  vWave = (dx + dy);

  vec2 px = uRect.xy + aPos * uRect.zw + vec2(dx, dy) * edge;
  vec2 clip = (px / uViewport) * 2.0 - 1.0;
  gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);
}
`;

const FRAG_SRC = `
precision mediump float;
uniform sampler2D uTex;
uniform vec2 uUvScale;
uniform vec2 uUvOffset;
// highp to match the vertex-side declaration — mismatched precision on a
// shared uniform is a link error on some GL implementations
uniform highp float uWave;
varying vec2 vUv;
varying float vWave;

void main() {
  vec2 uv = uUvOffset + vUv * uUvScale;

  // Chromatic fringe rides the wave, disappears when it settles
  float fringe = uWave * 0.012;
  float r = texture2D(uTex, uv + vec2(fringe, 0.0)).r;
  float g = texture2D(uTex, uv).g;
  float b = texture2D(uTex, uv - vec2(fringe, 0.0)).b;
  vec3 col = vec3(r, g, b);

  // Faint scanline while in flight
  col *= 1.0 - uWave * 0.08 * (0.5 + 0.5 * sin(gl_FragCoord.y * 3.14159));

  gl_FragColor = vec4(col, 1.0);
}
`;

function compileProgram(gl) {
  const make = (type, src) => {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      throw new Error(gl.getShaderInfoLog(s) || 'shader compile failed');
    }
    return s;
  };
  const prog = gl.createProgram();
  gl.attachShader(prog, make(gl.VERTEX_SHADER, VERT_SRC));
  gl.attachShader(prog, make(gl.FRAGMENT_SHADER, FRAG_SRC));
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    throw new Error(gl.getProgramInfoLog(prog) || 'program link failed');
  }
  return prog;
}

/** Subdivided unit-plane (GRID×GRID quads) as an indexed triangle mesh. */
function buildPlane(gl, prog, grid = 40) {
  const verts = [];
  for (let y = 0; y <= grid; y++) {
    for (let x = 0; x <= grid; x++) {
      verts.push(x / grid, y / grid);
    }
  }
  const idx = [];
  const row = grid + 1;
  for (let y = 0; y < grid; y++) {
    for (let x = 0; x < grid; x++) {
      const i = y * row + x;
      idx.push(i, i + 1, i + row, i + 1, i + row + 1, i + row);
    }
  }
  const vbo = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(verts), gl.STATIC_DRAW);
  const ibo = gl.createBuffer();
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(idx), gl.STATIC_DRAW);

  const loc = gl.getAttribLocation(prog, 'aPos');
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

  return idx.length;
}

/**
 * Wave-expand the card image from `r` to fullscreen on a WebGL canvas.
 * Resolves when the growth completes; the canvas stays up (flat, fullscreen)
 * until `cleanup()` is called. Returns null if WebGL isn't available —
 * callers fall back to the DOM layer.
 */
function createWaveExpand(r, { element = null, imageUrl = null } = {}, duration) {
  let gl, canvas;
  try {
    canvas = document.createElement('canvas');
    canvas.className = 'card-image-expand';
    canvas.setAttribute('aria-hidden', 'true');
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = Math.round(window.innerWidth * dpr);
    canvas.height = Math.round(window.innerHeight * dpr);
    Object.assign(canvas.style, {
      position: 'fixed',
      inset: '0',
      width: '100vw',
      height: '100vh',
      zIndex: '2000',
      pointerEvents: 'none',
      background: 'transparent',
    });
    gl = canvas.getContext('webgl', { alpha: true, antialias: true, premultipliedAlpha: true, preserveDrawingBuffer: true });
    if (!gl) return null;

    const prog = compileProgram(gl);
    gl.useProgram(prog);
    const indexCount = buildPlane(gl, prog);

    const U = (n) => gl.getUniformLocation(prog, n);
    const uRect = U('uRect'), uViewport = U('uViewport'), uWave = U('uWave'),
      uTime = U('uTime'), uUvScale = U('uUvScale'), uUvOffset = U('uUvOffset');

    // Texture — the card art is already on screen, so this is warm cache
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE,
      new Uint8Array([7, 7, 7, 255]));
    let imageAspect = r.width / r.height;
    const upload = (src) => {
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, src);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    };
    // Live card video: upload now and refresh every frame so the clip keeps
    // playing while it grows. Otherwise the poster image.
    const video = element && element.videoWidth ? element : null;
    if (video) {
      imageAspect = video.videoWidth / video.videoHeight;
      try { upload(video); } catch { /* tainted — stays dark */ }
    } else if (imageUrl) {
      const img = new Image();
      img.onload = () => { imageAspect = img.naturalWidth / img.naturalHeight; upload(img); };
      img.src = imageUrl;
    }

    document.body.appendChild(canvas);
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.clearColor(0, 0, 0, 0);
    gl.disable(gl.DEPTH_TEST);

    const start = { x: r.left, y: r.top, w: r.width, h: r.height };
    const full = { x: 0, y: 0, w: window.innerWidth, h: window.innerHeight };
    const proxy = { p: 0 };
    const t0 = performance.now();

    const draw = () => {
      const p = proxy.p;
      const x = gsap.utils.interpolate(start.x, full.x, p);
      const y = gsap.utils.interpolate(start.y, full.y, p);
      const w = gsap.utils.interpolate(start.w, full.w, p);
      const h = gsap.utils.interpolate(start.h, full.h, p);
      const wave = p * (1 - p) * 4; // 0 → 1 → 0, peaks mid-flight

      // background-size: cover
      const ra = w / h;
      let sx = 1, sy = 1;
      if (ra > imageAspect) sy = imageAspect / ra;
      else sx = ra / imageAspect;

      if (video && !video.paused && video.readyState >= 2) {
        try { upload(video); } catch { /* ignore */ }
      }
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.uniform4f(uRect, x * (canvas.width / full.w), y * (canvas.height / full.h),
        w * (canvas.width / full.w), h * (canvas.height / full.h));
      gl.uniform2f(uViewport, canvas.width, canvas.height);
      gl.uniform1f(uWave, wave);
      gl.uniform1f(uTime, (performance.now() - t0) / 1000);
      gl.uniform2f(uUvScale, sx, sy);
      gl.uniform2f(uUvOffset, (1 - sx) / 2, (1 - sy) / 2);
      gl.drawElements(gl.TRIANGLES, indexCount, gl.UNSIGNED_SHORT, 0);
    };
    draw();

    const grown = new Promise((resolve) => {
      gsap.to(proxy, {
        p: 1,
        duration,
        ease: 'power2.inOut',
        overwrite: true,
        onUpdate: draw,
        onComplete: () => {
          draw(); // final flat fullscreen frame
          resolve();
        },
      });
    });

    const cleanup = () => {
      try { gl.getExtension('WEBGL_lose_context')?.loseContext(); } catch { /* fine */ }
      canvas.remove();
    };

    return { layer: canvas, grown, cleanup };
  } catch (e) {
    transitionLog('waveExpand:webgl_failed', { error: String(e) });
    canvas?.remove();
    return null;
  }
}

// ── DOM fallback (previous behavior) ──────────────────────────

function createImageLayer(r, imageUrl) {
  const layer = document.createElement('div');
  layer.className = 'card-image-expand';
  layer.setAttribute('aria-hidden', 'true');
  Object.assign(layer.style, {
    position: 'fixed',
    top: `${r.top}px`,
    left: `${r.left}px`,
    width: `${r.width}px`,
    height: `${r.height}px`,
    margin: '0',
    zIndex: '2000',
    borderRadius: '6px',
    overflow: 'hidden',
    pointerEvents: 'none',
    backgroundColor: '#070707',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundImage: imageUrl ? `url(${imageUrl})` : 'none',
    transform: 'none',
    transition: 'none',
  });
  document.body.appendChild(layer);
  return layer;
}

function domGrow(layer, duration) {
  return new Promise((resolve) => {
    gsap.to(layer, {
      top: 0, left: 0, width: '100vw', height: '100vh', borderRadius: 0,
      duration, ease: 'sine.inOut', overwrite: true,
      onComplete: resolve,
    });
  });
}

// ── public API (unchanged signatures) ─────────────────────────

/**
 * MPA: grow image layer to fullscreen, then navigate.
 * @param {HTMLElement} cardEl
 * @param {string} url
 * @returns {Promise<void>}
 */
export function expandCardImageThenNavigate(cardEl, url) {
  const r = cardEl.getBoundingClientRect();
  const src = getCardVisualSource(cardEl);
  transitionLog('expandFromCard:image', { path: String(url), image: src.imageUrl, video: !!src.element });

  const wave = REDUCED_MOTION ? null : createWaveExpand(r, src, MPA_EXPAND_DURATION);
  const grown = wave ? wave.grown : domGrow(createImageLayer(r, captureFrameDataUrl(src.element) || src.imageUrl), MPA_EXPAND_DURATION);

  return grown.then(() => {
    transitionLog('expandFromCard:navigate', { path: String(url) });
    // Leave the layer up — the next document loads over it.
    window.location.assign(String(url));
  });
}

/**
 * In-place world: fullscreen grow, then `onOpen(fullscreenLayer)` runs,
 * then the cover layer is removed.
 * @param {HTMLElement} cardEl
 * @param {{ top: number; left: number; width: number; height: number }} rect
 * @param {(layer: HTMLElement) => void | Promise<void>} onOpen
 * @returns {{ layer: HTMLElement, promise: Promise<void> }}
 */
export function expandCardImageInPlace(cardEl, rect, onOpen) {
  const src = getCardVisualSource(cardEl);
  transitionLog('expandInPlace:image', { image: src.imageUrl, video: !!src.element });

  const duration = INPLACE_EXPAND_DURATION * getRevealSlowMult();
  const wave = REDUCED_MOTION ? null : createWaveExpand(rect, src, duration);
  const layer = wave ? wave.layer : createImageLayer(rect, captureFrameDataUrl(src.element) || src.imageUrl);
  const grown = wave ? wave.grown : domGrow(layer, duration);

  const promise = grown.then(async () => {
    transitionLog('expandInPlace:grow_done', { slowMult: getRevealSlowMult(), webgl: !!wave });
    try {
      if (onOpen) await Promise.resolve(onOpen(layer));
    } catch (e) {
      console.warn('[cardImageExpand] onOpen failed:', e);
    } finally {
      if (wave) wave.cleanup();
      else if (layer.parentNode) layer.remove();
    }
  });

  return { layer, promise };
}

/** @deprecated use INPLACE_EXPAND_DURATION — world open is after grow + onOpen */
export const INPLACE_OPEN_AT = INPLACE_EXPAND_DURATION;
